import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { authService } from '../services/auth';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await authService.forgotPassword({ email });
      setMessage(response.message);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'An error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full space-y-6 sm:space-y-8'>
        {/* Brand Section */}
        <div className='text-center'>
          <div className='flex justify-center mb-4'>
            <div className='w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-2xl flex items-center justify-center shadow-lg'>
              <span className='text-white font-bold text-2xl'>P</span>
            </div>
          </div>
          <h2 className='heading-2 text-gray-900 mb-2'>Forgot Password</h2>
          <p className='body-small text-gray-600'>
            Enter your email address and we'll send you a link to reset your
            password
          </p>
        </div>

        <Card className='card-enhanced shadow-xl border-0'>
          <div className='p-6 sm:p-8'>
            {/* Success Message */}
            {message && (
              <div className='mb-6 p-4 bg-green-50 border border-green-200 rounded-lg'>
                <p className='text-sm text-green-800'>{message}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg'>
                <p className='text-sm text-red-800'>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className='space-y-6'>
              <div className='form-field'>
                <Label htmlFor='email' className='form-label'>
                  Email Address
                </Label>
                <Input
                  id='email'
                  name='email'
                  type='email'
                  autoComplete='email'
                  required
                  placeholder='Enter your email address'
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={loading}
                  className='form-input focus-ring transition-smooth'
                />
              </div>

              <Button
                type='submit'
                disabled={loading || !email}
                className='btn-primary w-full h-12 text-base font-medium'
              >
                {loading ? (
                  <div className='flex items-center justify-center'>
                    <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2'></div>
                    Sending Reset Link...
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>

            {/* Back to Login */}
            <div className='mt-6 text-center'>
              <p className='body-small text-gray-600'>
                Remember your password?{' '}
                <Link
                  to='/login'
                  className='font-medium text-primary hover:text-primary/80 transition-colors'
                >
                  Back to Login
                </Link>
              </p>
            </div>
          </div>
        </Card>

        {/* Help Section */}
        <div className='text-center'>
          <p className='body-small text-gray-500'>
            Need help? Contact our{' '}
            <a
              href='mailto:support@partnergpt.com'
              className='text-primary hover:text-primary/80 transition-colors'
            >
              support team
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
